import React, { useState, useMemo } from 'react';
import { X, UtensilsCrossed, Clock, Package, MessageSquare, ChevronDown, ChevronUp, List, Layers } from 'lucide-react';
import { useKOT } from '../../../hooks/useKOT';

export default function ViewOrdersModal({ tableId, tableSplit, tableName, onClose }) {
  // View mode: 'kot' (grouped by KOT) or 'items' (flat list)
  const [viewMode, setViewMode] = useState('items');

  // State to track which KOTs are expanded (default: all expanded)
  const [expandedKOTs, setExpandedKOTs] = useState({});

  // Fetch active orders for this table/split
  const { activeOrders, loading } = useKOT(
    tableId,
    tableSplit,
    { skipIfNew: false }
  );

  // Group items by KOT
  const kotGroups = (activeOrders || []).map(kot => ({
    kotNumber: kot.kotNumber || kot.orderNumber,
    kotId: kot.id,
    createdAt: kot.createdAt || kot.createdDateTime,
    status: kot.status || kot.orderStatus,
    notes: kot.notes,
    waiterName: kot.waiterName,
    items: (kot.items || []).map(item => ({
      id: item.id,
      name: item.itemName || item.name,
      price: item.unitPrice || item.price || 0,
      quantity: item.quantity || 1,
      variantName: item.variantName,
      station: item.kitchenStationName || item.station,
      itemStatus: item.itemStatus,
      customization: item.customization,
      specialInstructions: item.specialInstructions || item.customization?.customInstructions,
    }))
  }));

  // Initialize all KOTs as collapsed
  React.useEffect(() => {
    const initialState = {};
    kotGroups.forEach(kot => {
      initialState[kot.kotId] = false; // All collapsed by default
    });
    setExpandedKOTs(initialState);
  }, [activeOrders]);

  const toggleKOT = (kotId) => {
    setExpandedKOTs(prev => ({
      ...prev,
      [kotId]: !prev[kotId]
    }));
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // KOT level status badge
  const getKotStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    const badges = {
      'sent': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
      'sent to kitchen': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Sent' },
      'in_progress': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Cooking' },
      'cooking': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Cooking' },
      'completed': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Ready' },
      'ready': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Ready' },
    };
    const badge = badges[statusLower] || { bg: 'bg-slate-100', text: 'text-slate-600', label: status || 'Pending' };
    return (
      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Item level status badge
  const getItemStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    const badges = {
      'pending': { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Pending' },
      'preparing': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Preparing' },
      'ready': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Ready' },
      'served': { bg: 'bg-teal-100', text: 'text-teal-700', label: 'Served' },
    };
    const badge = badges[statusLower] || badges['pending'];
    return (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const totalItems = kotGroups.reduce((sum, kot) => sum + kot.items.length, 0);

  // Flat items list - merge same items and sum quantities
  const flatItems = useMemo(() => {
    const itemMap = {};

    kotGroups.forEach(kot => {
      kot.items.forEach(item => {
        // Create a unique key based on item name and variant
        const key = `${item.name}-${item.variantName || 'default'}`;

        if (itemMap[key]) {
          itemMap[key].quantity += item.quantity;
          itemMap[key].totalPrice += item.price * item.quantity;
        } else {
          itemMap[key] = {
            id: item.id,
            name: item.name,
            variantName: item.variantName,
            price: item.price,
            quantity: item.quantity,
            totalPrice: item.price * item.quantity,
            station: item.station,
          };
        }
      });
    });

    return Object.values(itemMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [kotGroups]);

  // Calculate grand total
  const grandTotal = flatItems.reduce((sum, item) => sum + item.totalPrice, 0);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">

        {/* Compact Header */}
        <div className="bg-teal-600 text-white px-4 py-3 flex items-center gap-3 border-b border-teal-700">
          {/* Icon */}
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <UtensilsCrossed size={20} />
          </div>

          {/* Title */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold truncate">Table {tableName}</h3>
            <p className="text-xs text-teal-100">
              {kotGroups.length} KOTs • {totalItems} items • ₹{grandTotal.toFixed(0)}
            </p>
          </div>

          {/* View Toggle */}
          <div className="flex bg-white/20 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('items')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'items'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <List size={14} />
              Items
            </button>
            <button
              onClick={() => setViewMode('kot')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                viewMode === 'kot'
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <Layers size={14} />
              By KOT
            </button>
          </div>

          {/* Close */}
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors flex-shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-teal-600 border-t-transparent"></div>
              <p className="mt-3 text-slate-600 text-sm">Loading orders...</p>
            </div>
          )}

          {!loading && kotGroups.length === 0 && (
            <div className="text-center py-12">
              <Package size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No active orders found</p>
            </div>
          )}

          {/* Flat Items View */}
          {!loading && kotGroups.length > 0 && viewMode === 'items' && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              {flatItems.map((item, index) => (
                <div
                  key={`${item.name}-${item.variantName || index}`}
                  className="px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    {/* Quantity Badge */}
                    <div className="w-8 h-8 bg-teal-100 text-teal-700 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {item.quantity}
                    </div>

                    {/* Item Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-slate-900 text-sm">
                          {item.name}
                        </h4>
                        {item.variantName && (
                          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            {item.variantName}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs text-slate-500">₹{item.price.toFixed(0)} each</span>
                        {item.station && (
                          <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                            📍 {item.station}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Total Price */}
                    <div className="text-right flex-shrink-0">
                      <p className="font-bold text-slate-900">
                        ₹{item.totalPrice.toFixed(0)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Total Row */}
              <div className="px-4 py-3 bg-teal-50 border-t-2 border-teal-200">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-700">Total ({flatItems.reduce((s, i) => s + i.quantity, 0)} items)</span>
                  <span className="text-lg font-bold text-teal-700">₹{grandTotal.toFixed(0)}</span>
                </div>
              </div>
            </div>
          )}

          {/* KOT Grouped View */}
          {!loading && kotGroups.length > 0 && viewMode === 'kot' && (
            <div className="space-y-2">
              {kotGroups.map((kot, kotIndex) => {
                const isExpanded = expandedKOTs[kot.kotId];

                return (
                  <div
                    key={kot.kotId}
                    className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:border-teal-300 transition-colors"
                  >
                    {/* Collapsible KOT Header */}
                    <button
                      onClick={() => toggleKOT(kot.kotId)}
                      className="w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors border-b border-slate-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-teal-600 text-white rounded-md flex items-center justify-center font-bold text-sm">
                            {kotIndex + 1}
                          </div>
                          <div className="text-left">
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-slate-900 text-sm">#{kot.kotNumber}</p>
                              {getKotStatusBadge(kot.status)}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                              <div className="flex items-center gap-1">
                                <Clock size={11} />
                                <span>{formatTime(kot.createdAt)}</span>
                              </div>
                              <span>•</span>
                              <span>{kot.items.length} {kot.items.length === 1 ? 'item' : 'items'}</span>
                              {kot.waiterName && (
                                <>
                                  <span>•</span>
                                  <span className="text-teal-600 font-medium">{kot.waiterName}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">
                            ₹{kot.items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(0)}
                          </span>
                          {isExpanded ? (
                            <ChevronUp size={18} className="text-slate-400" />
                          ) : (
                            <ChevronDown size={18} className="text-slate-400" />
                          )}
                        </div>
                      </div>

                      {/* KOT Notes */}
                      {kot.notes && (
                        <div className="mt-2 text-left">
                          <p className="text-xs text-amber-700 italic">
                            Note: "{kot.notes}"
                          </p>
                        </div>
                      )}
                    </button>

                    {/* Expandable KOT Items */}
                    {isExpanded && (
                      <div className="bg-white">
                        {kot.items.map((item, itemIndex) => (
                          <div
                            key={`${kot.kotId}-${item.id}-${itemIndex}`}
                            className="px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-3">
                              {/* Item Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold text-slate-900 text-sm">
                                    {item.name}
                                  </h4>
                                  {item.variantName && (
                                    <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                      {item.variantName}
                                    </span>
                                  )}
                                  {getItemStatusBadge(item.itemStatus)}
                                </div>

                                {/* Station & Instructions */}
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {item.station && (
                                    <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                                      📍 {item.station}
                                    </span>
                                  )}
                                  {item.specialInstructions && (
                                    <span className="text-[10px] text-amber-700 flex items-center gap-1">
                                      <MessageSquare size={10} />
                                      <span className="italic">"{item.specialInstructions}"</span>
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Quantity & Price */}
                              <div className="text-right flex-shrink-0">
                                <p className="text-xs text-slate-500">
                                  {item.quantity} × ₹{item.price.toFixed(0)}
                                </p>
                                <p className="font-bold text-slate-900 text-sm">
                                  ₹{(item.price * item.quantity).toFixed(0)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-3 bg-white">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
